import { useCallback, useEffect, useRef, useState } from 'react'
import { IconButton, Stack } from '@mui/material'
import { styled } from '@mui/material/styles'
import { getFileFromUser } from '@weblite/file'
import { RawVideoMessage } from '@weblite/ts-action/message'
import { AppBarBackTitleReplace } from '$common/app-bar/app-bar-back-title-replace'
import { useContentFileStatus } from '$common/react-hooks/message'
import { useFileObjectUrl } from '$func/file'
import { nothing } from '$func/utils'
import { PauseIcon } from '$common/icons/pause'
import { PlayIcon } from '$common/icons/play'
import { t } from '$i18n'
import { MediaCard } from '$common/media-card'

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: calc(50% - 25px);
  left: calc(50% - 25px);
  width: 50px;
  height: 50px;

  border-radius: 50%;
  background: rgba(80, 89, 98, 0.7);
  z-index: 1;
`

const StyledVideo = styled(`video`)`
  width: 100%;
  height: 100%;

  position: absolute;
  object-fit: contain;
`

interface Props {
  attachment: RawVideoMessage['attachment']
  canReplace?: boolean
  onCancel: () => unknown
  setSelectedFile: (file: File) => unknown
  newFile?: File
}

export function EditVideo({
  attachment,
  canReplace,
  onCancel,
  setSelectedFile,
  newFile,
}: Props) {
  const {
    fileId,
    base64: thumbnail,
    width,
    height,
    thumbnailFileId,
  } = attachment

  const [marginTop, setMarginTop] = useState(0)
  const [marginRight, setMarginRight] = useState(0)
  const [maxHeight, setMaxHeight] = useState<number>()

  const [controllerIsVisible, setControllerIsVisible] = useState(true)
  const [isPlaying, setIsPlaying] = useState(false)

  const cardRef = useRef<HTMLDivElement>(null)
  const videoContainerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const controllerRef = useRef<HTMLButtonElement>(null)

  const newFileObjectUrl = useFileObjectUrl(newFile)

  if (!fileId) return null

  const { blobURL: videoBlobUrl, startDownload } = useContentFileStatus({
    fileId,
    uploadId: '',
    messageType: 'IMAGE',
  })

  const { blobURL: thumbnailBlobUrl } = useContentFileStatus({
    uploadId: '',
    fileId: thumbnailFileId || '',
    messageType: 'IMAGE',
  })

  const videoSource = newFileObjectUrl || videoBlobUrl
  const poster = !videoSource ? thumbnailBlobUrl || thumbnail : undefined

  useEffect(() => {
    if (newFileObjectUrl || videoBlobUrl) return
    startDownload()
  }, [newFileObjectUrl, videoBlobUrl, startDownload])

  useEffect(() => {
    handlePosition()
  })

  useEffect(() => {
    if (!newFile) return
    const supportsVideo = !!videoRef.current?.canPlayType(newFile?.type)
    if (supportsVideo) {
      setControllerIsVisible(true)
    }
  }, [newFile])

  const handlePosition = () => {
    const videoContainer = videoContainerRef.current
    const video = videoRef.current
    const card = cardRef.current
    if (!videoContainer || !video || !card) return

    if (width >= height) {
      videoContainer.style.width = '100%'
      videoContainer.style.height = 'auto'
      const offset = card.clientHeight - video.clientHeight
      setMarginTop(offset > 0 ? offset / 2 : 0)
    } else {
      videoContainer.style.height = '100%'
      videoContainer.style.width = 'auto'
      const offset = card.clientWidth - video.clientWidth
      setMarginRight(offset > 0 ? offset / 2 : 0)
    }
  }

  const handleLoad = useCallback(() => {
    if (cardRef.current) setMaxHeight(cardRef.current.clientHeight)
    handlePosition()
  }, [fileId, newFile])

  const togglePlay = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    if (videoRef.current.paused || videoRef.current.ended) {
      videoRef.current.play()?.catch(nothing)
      setIsPlaying(true)
    } else {
      videoRef.current.pause()
      setIsPlaying(false)
    }
  }, [videoRef, controllerRef])

  return (
    <MediaCard
      sxProps={{ position: 'fixed', top: 0 }}
      cardRef={cardRef}
      appbarComponent={
        <AppBarBackTitleReplace
          title={t('chat.editVideo')}
          canReplace={canReplace}
          onBack={onCancel}
          onReplace={() =>
            getFileFromUser('video/*', (file: File | File[]) => {
              setSelectedFile(Array.isArray(file) ? file[0] : file)
            })
          }
        />
      }
    >
      <Stack
        ref={videoContainerRef}
        sx={{
          marginTop: `${marginTop}px`,
          maxHeight,
          marginRight: `${marginRight}px`,
        }}
      >
        <StyledIconButton
          style={{ display: controllerIsVisible ? 'flex' : 'none' }}
          ref={controllerRef}
          onClick={togglePlay}
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </StyledIconButton>

        <StyledVideo
          ref={videoRef}
          src={videoSource}
          onLoadedData={handleLoad}
          poster={poster}
        />
      </Stack>
    </MediaCard>
  )
}
